import { z } from 'zod'

export const PROTOCOL_VERSION = 1 as const

/** Frame types. Client→broker and broker→client unioned; routing uses `topic` only. */
export const CLIENT_FRAME_TYPES = [
  'auth',
  'subscribe',
  'unsubscribe',
  'location',
  'telemetry',
  'lidar',
  'alert',
  'command',
  'ack',
  'ping',
  'pong',
] as const
export const SERVER_FRAME_TYPES = [
  'welcome',
  'subscribed',
  'unsubscribed',
  'snapshot',
  'location',
  'telemetry',
  'lidar',
  'alert',
  'command',
  'ack',
  'error',
  'ping',
  'pong',
] as const
export type ClientFrameType = (typeof CLIENT_FRAME_TYPES)[number]
export type ServerFrameType = (typeof SERVER_FRAME_TYPES)[number]

/** Closed error-code enum — spec §6. */
export const PROTOCOL_ERROR_CODES = [
  'auth_timeout',
  'auth_invalid',
  'auth_expired',
  'tenant_invalid',
  'forbidden_topic',
  'forbidden_role',
  'robot_offline',
  'rate_limited',
  'slow_consumer',
  'bad_envelope',
  'unknown_type',
] as const
export type ProtocolErrorCode = (typeof PROTOCOL_ERROR_CODES)[number]

/** Fatal codes close the socket; the rest reject the frame and keep it open. */
export const FATAL_ERROR_CODES = new Set<ProtocolErrorCode>([
  'auth_timeout',
  'auth_invalid',
  'auth_expired',
  'tenant_invalid',
  'bad_envelope',
  'slow_consumer',
])

export interface Envelope<T = unknown> {
  v: 1
  id: string
  type: ClientFrameType | ServerFrameType
  topic?: string
  robotId?: string
  seq?: number
  reId?: string
  ts?: number
  payload?: T
}

const ALL_TYPES = new Set<string>([...CLIENT_FRAME_TYPES, ...SERVER_FRAME_TYPES])

export function isClientFrame(value: unknown): value is Envelope {
  if (value === null || typeof value !== 'object') return false
  const e = value as Record<string, unknown>

  return (
    e['v'] === PROTOCOL_VERSION &&
    typeof e['id'] === 'string' &&
    typeof e['type'] === 'string' &&
    ALL_TYPES.has(e['type'] as string)
  )
}

// `tenant` is OPTIONAL: a tenant robot carries its DNS-label tenant (the broker
// must be told which Mongo DB to query), while a CENTRAL-domain robot omits it
// entirely (its RobotDevice lives in the core DB with no tenantId) — mirroring
// the central HTTPS location path (`update-pose.ts` with `ctx.tenantId = null`).
const robotAuth = z.object({ token: z.string().min(3), tenant: z.string().min(1).optional() })
const operatorAuth = z.object({ jwt: z.string().min(3) })

export type AuthFrame = { kind: 'robot'; token: string; tenant?: string } | { kind: 'operator'; jwt: string }

export function parseAuthFrame(envelope: unknown): AuthFrame {
  if (!isClientFrame(envelope) || envelope.type !== 'auth') {
    throw new Error('not an auth frame')
  }

  const payload = (envelope as Envelope).payload
  const robot = robotAuth.safeParse(payload)
  if (robot.success) return { kind: 'robot', token: robot.data.token, tenant: robot.data.tenant }
  const op = operatorAuth.safeParse(payload)
  if (op.success) return { kind: 'operator', jwt: op.data.jwt }
  throw new Error('auth frame must carry {token} (+optional tenant — robot) or {jwt} (operator)')
}
